import * as React from 'react';
import './tabular.scss';
import {
  cn,
  formatDate,
  getCardFields,
  getEmployeeName,
  getMetadata,
  isAuditType,
  parseDate,
  parseField
} from '../../../utils';
import { FieldType } from '../../../enums/FieldType';
import { Pill, Pills } from '../../Pill/pill';
import { useActions as useFormActions } from '../../MetadataForm/metadataFormActions';
import { useActions as useTabularActions } from './tabularActions';
import {
  FieldSchemaInfo,
  Metadata,
  Schema,
  SchemaField
} from '../../../types/Metadata';
import { useActions } from '../viewActions';
import { View } from '../../../types/View';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { DataType } from '../../../enums/DataType';
import { Icon, Presentation, Trash } from 'rcd-cpp-ui/ui/icon';
import InfiniteLoader from '../../InfiniteLoader/infiniteLoader';
import { ConfirmModal } from '../../ConfirmModal/confirmModal';
import { NodeType } from '../../../enums/NodeType';
import {
  TrueCheck,
  FalseCheck,
  GIcon,
  DocSearch,
  GoogleDoc,
  SixDots,
  Form,
  LargePencil
} from '../../Icon/icon';
import URLManager from '../../../utils/urlManager';
import { useSelector } from '../../../utils/useSelector';
import { useLoader } from '../../Loader/loader';
import { useAlert } from '../../Alert/alert';
import { useDrag, useDrop } from 'react-dnd';
import {
  EditModeDropdown,
  generateBoxAdjustments
} from '../EditMode/editModeDropdown';
import AccessControl from '../../AccessControl/accessControl';
import { TreeFieldUtils } from '../../../utils/treeFieldUtils';
import {
  createUpdater,
  handleDisableEdit,
  updateSelectedCellPosition,
  updateHoveredCellPosition,
  handleScrollIntoView
} from '../../../utils/editModeUtils';
import { SelectedCell } from '../../../types/SelectedCell';
import { ToasterWithLink, useToaster } from '../../Toaster/toaster';
import { Warning } from 'rcd-cpp-ui/ui/icon';
import { UserSetting } from '../../../types/UserSetting';
import { ColField } from '../../../types/ColField';
import { ContextMenuLink } from '../../ContextMenuLink/contextMenuLink';

const createHeaders = (
  headers: string[],
  headerType: string[],
  fields: ColField[],
  refs: React.RefObject<HTMLTableCellElement>[]
) => {
  return headers.map((item, i) => ({
    text: item,
    colType: headerType[i],
    field: fields[i].schemaInfo.schemaField.key,
    ref: refs[i]
  }));
};

type Props = {
  increaseNumOfResultsAndLoad?: (cb?: Function) => any;
  showForm?: (data: Partial<Metadata>, s?: Schema) => any;
  onDelete?: (data: Partial<Metadata>, s?: Schema) => any;
  additionalSchemas?: Schema[];
  disabled?: boolean;
  readOnly?: boolean;
  showCheckbox?: boolean;
};

const getActions = (props: Props) => {
  const { increaseNumOfResultsAndLoad, toggleCheckbox, setViewSettings } =
    useActions();
  const { deleteData } = useFormActions();
  return {
    increaseNumOfResultsAndLoad,
    deleteData,
    toggleCheckbox,
    setViewSettings,
    ...props
  };
};

export type TreeGrouping = {
  parentField: SchemaField;
  childField: SchemaField;
};

export const Table = ({
  disabled,
  readOnly,
  additionalSchemas,
  showCheckbox,
  ...props
}: Props) => {
  const minCellWidth = 120;
  const getMountPoint = useSelector(s => s.app?.elFn?.getMountPoint);
  const _schemas = useSelector(
    s => s.planning?.selectedWorkspace?.schemas || []
  );
  const card = useSelector(s => s.view?.selected?.card);
  const dataSchema = useSelector(s => s.view?.selected?.dataSchema);
  const data = useSelector(s => s.view?.data);
  const pagination = useSelector(s => s.view?.pagination);
  const searchHash = useSelector(s => s.view?.searchHash);
  const selectedItem = useSelector(s => s.form?.data?.id);
  const isCheckedAll = useSelector(s => !!s.view?.isCheckedAll);
  const toggledRefIds = useSelector(s => s.view?.toggledRefIds);
  const refIdsCount = 1; //useSelector(s => s.view?.info?.refIds?.length);
  const isMyItemsView = useSelector(s => s.view?.selected?.isMyItemsView);
  const editMode = useSelector(s => s.view?.editMode);
  const invalidCells = useSelector(s => s.view?.invalidCells);
  const userSetting = useSelector(
    s => s.planning?.userSettings[s.view?.selected?.props?.refId]
  );
  const wrapText = userSetting?.props?.wrapText;
  const [dataToDelete, setDataToDelete] = React.useState<DataToDelete>(null);
  const [tableHeight, setTableHeight] = React.useState<string>('auto');
  const [activeIndex, setActiveIndex] = React.useState<number>(null);
  const tableElement = React.useRef(null);
  const { updateUrl, wid, vid, c, m } = URLManager.useRouter();
  const schemas = additionalSchemas
    ? additionalSchemas.concat(_schemas)
    : _schemas;

  const { createToaster } = useToaster(ToasterWithLink);
  const toasters = createToaster();
  const {
    showForm,
    deleteData,
    increaseNumOfResultsAndLoad,
    onDelete,
    toggleCheckbox,
    setViewSettings
  } = getActions(props);

  const allowDelete =
    !readOnly &&
    !disabled &&
    !dataSchema?.isSystemSchema &&
    !dataSchema?.isInherited &&
    ![
      NodeType.RolePriority,
      NodeType.PriorityJourneyStep,
      NodeType.Position
    ].includes(dataSchema?.props?.type);

  const { fields: orderedFields, columns: orderedColumns } = getTableInfo(
    card,
    dataSchema,
    schemas,
    userSetting
  );
  const totalColumnCount =
    orderedColumns.length + (showCheckbox ? 1 : 0) + (allowDelete ? 1 : 0);

  const [draggedColIdx, setDraggedColIdx] = React.useState(null);
  const [hoveredColIdx, setHoveredColIdx] = React.useState(null);
  const [columnWidths, setColumnWidths] = React.useState<{
    [key: string]: number;
  }>({});

  const moveColumn = (fromIdx: number, toIdx: number) => {
    if (fromIdx == toIdx) return;
    const newOrder = [...orderedColumns];
    const [removed] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, removed);
    setViewSettings({
      fields: newOrder.map(c => c.field)
    });
  };

  const openItem = React.useCallback(
    (item: Metadata, refId?: string) => {
      const targetRefId = refId || item?.props?.refId;
      if (!targetRefId) return;
      if (showForm) showForm(item);
      else updateUrl({ id: targetRefId });
    },
    [showForm, updateUrl]
  );

  const getItemHref = React.useCallback(
    (refId: string) => {
      if (!wid || !refId) return null;
      return URLManager.generateUrl(wid, { v: vid, c, m, id: refId });
    },
    [wid, vid, c, m]
  );
  React.useEffect(() => {
    setTableHeight(tableElement?.current?.offsetHeight);
  }, []);
  const _window = getMountPoint ? getMountPoint() : window;
  const mouseMove = React.useCallback(
    e => {
      const navFlyout = document.getElementById('navFlyout')?.offsetWidth;
      const gridColumns = orderedColumns.map((col, i) => {
        if (i === activeIndex) {
          const width = e.clientX - navFlyout - col.ref.current.offsetLeft;
          if (width >= minCellWidth) {
            setColumnWidths(prev => ({
              ...prev,
              [col.field]: width
            }));
            const storedWidth = columnWidths[col.field];
            return storedWidth
              ? `${storedWidth}px`
              : `${col.ref.current.offsetWidth}px`;
          }
        }
        return `${col.ref.current.offsetWidth}px`;
      });
      if (allowDelete) gridColumns.push('21px');
      if (showCheckbox) gridColumns.unshift('25px');

      tableElement.current.style.gridTemplateColumns = `${gridColumns.join(
        ' '
      )}`;
    },
    [activeIndex, orderedColumns, minCellWidth, columnWidths]
  );

  const removeListeners = React.useCallback(() => {
    _window.removeEventListener('mousemove', mouseMove);
    _window.removeEventListener('mouseup', removeListeners);
  }, [mouseMove, _window]);

  const mouseUp = React.useCallback(() => {
    setActiveIndex(null);
    removeListeners();
  }, [setActiveIndex, removeListeners, _window]);

  React.useEffect(() => {
    if (activeIndex !== null) {
      _window.addEventListener('mousemove', mouseMove);
      _window.addEventListener('mouseup', mouseUp);
    }

    return () => {
      removeListeners();
    };
  }, [activeIndex, mouseMove, mouseUp, removeListeners, _window]);

  const ref = React.useRef(null);

  const viewClassName = !!isMyItemsView ? 'fs-mask' : '';

  const isItemChecked = React.useCallback(
    (refId: string) => {
      const refIds = toggledRefIds || [];
      return (
        (isCheckedAll && !refIds.includes(refId)) ||
        (!isCheckedAll && refIds.includes(refId))
      );
    },
    [isCheckedAll, toggledRefIds]
  );

  const [hoveredCell, setHoveredCell] = React.useState(null);
  const [selectedCell, setSelectedCell] = React.useState<SelectedCell>(null);
  const [selectedCellPosition, setSelectedCellPosition] = React.useState(null);
  const [hoveredCellPosition, setHoveredCellPosition] = React.useState(null);
  const [isTreeSelect, setIsTreeSelect] = React.useState<boolean>(null);
  const [updateCellPositionTrigger, setUpdateCellPositionTrigger] =
    React.useState<boolean>(false);
  const [editValue, setEditValue] = React.useState(null);
  const [editValueTree, setEditValueTree] = React.useState<Partial<Metadata>>({
    props: {}
  });
  const [originalValueTree, setOriginalValueTree] = React.useState<
    Partial<Metadata>
  >({
    props: {}
  });
  const updater = createUpdater(setEditValueTree);

  const [treeFieldGroupings, setTreeFieldGroupings] =
    React.useState<TreeGrouping[]>(null);
  const [parentTreeField, setParentTreeField] =
    React.useState<SchemaField>(null);

  const minEditBoxWidth = Math.max(selectedCellPosition?.width * 1.5, 300);
  const navFlyout = document?.getElementById('navFlyout');
  const navFlyoutPos = navFlyout?.getBoundingClientRect();
  const navFlyoutWidth = Math.abs(navFlyoutPos?.right - navFlyoutPos?.left) + 6;
  const { updateField, updateData, updateFields } = useTabularActions();
  const [originalValue, setOriginalValue] = React.useState(null);

  React.useEffect(() => {
    setHoveredCell(null);
    setSelectedCell(null);
  }, [editMode]);

  React.useEffect(() => {
    const schemaFields = dataSchema?.props?.fields;
    const treeFieldGrouping: TreeGrouping[] = [];
    schemaFields?.forEach(sf => {
      const treeSetting = TreeFieldUtils.getTreeSetting(
        dataSchema,
        sf,
        schemas
      );
      if (treeSetting?.settings?.length) {
        const parentField = treeSetting?.settings?.shift();
        treeSetting?.settings?.forEach(s => {
          const treeGrouping: TreeGrouping = {
            parentField: parentField?.field,
            childField: s?.field
          };
          treeFieldGrouping.push(treeGrouping);
        });
      }
    });
    setTreeFieldGroupings(treeFieldGrouping);
  }, []);

  React.useEffect(() => {
    setUpdateCellPositionTrigger(false);
    updateSelectedCellPosition(document, setSelectedCellPosition);
    setEditValueTree({ props: { ...selectedCell?.item?.props } });
    setOriginalValueTree({ props: { ...selectedCell?.item?.props } });
  }, [selectedCell]);
  React.useEffect(() => {
    handleScrollIntoView(
      generateBoxAdjustments,
      selectedCellPosition,
      minEditBoxWidth,
      selectedCell,
      isTreeSelect,
      window,
      tableElement,
      document,
      setUpdateCellPositionTrigger,
      navFlyoutWidth,
      totalColumnCount
    );
  }, [selectedCellPosition]);
  React.useEffect(() => {
    if (updateCellPositionTrigger == true) {
      updateSelectedCellPosition(document, setSelectedCellPosition);
    }
  }, [updateCellPositionTrigger]);
  React.useEffect(() => {
    updateHoveredCellPosition(document, setHoveredCellPosition);
  }, [hoveredCell]);

  const handleMouseEnter = index => {
    setHoveredCell(index);
  };
  const handleMouseLeave = () => {
    setHoveredCell(null);
  };
  const { addInvalidCell, removeInvalidCell, updatesInvalidCells } =
    useActions();
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);

  return (
    <div className="tabular" id="tabular" ref={ref} key={searchHash}>
      <div className="table-wrapper">
        <table
          className={cn('resizeable-table', {
            'removable-table': allowDelete,
            'readonly-table': readOnly,
            wrap: wrapText,
            'no-wrap': !wrapText
          })}
          style={{
            gridTemplateColumns: getGridTemplateColumns(
              orderedColumns,
              columnWidths,
              showCheckbox,
              allowDelete
            ).join(' ')
          }}
          ref={tableElement}
        >
          <thead>
            <tr>
              {showCheckbox && (
                <th className="with-cb">
                  {refIdsCount > 0 && (
                    <input
                      type="checkbox"
                      checked={isCheckedAll}
                      onClick={() => toggleCheckbox()}
                    />
                  )}
                </th>
              )}
              {orderedColumns.map((col, i) => (
                <DraggableHeader
                  col={col}
                  idx={i}
                  key={col.field}
                  moveColumn={moveColumn}
                  setDraggedColIdx={setDraggedColIdx}
                  setHoveredColIdx={setHoveredColIdx}
                  draggedColIdx={draggedColIdx}
                  hoveredColIdx={hoveredColIdx}
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  tableHeight={tableHeight}
                  wrapText={wrapText}
                />
              ))}
              {allowDelete && <th></th>}
            </tr>
          </thead>
          <tbody className={viewClassName}>
            {data?.map(item => {
              const refId = item.props.refId;
              return (
                <tr
                  key={refId}
                  onClick={readOnly || editMode ? null : () => openItem(item)}
                  data-id={item.id}
                  className={cn(
                    {
                      'select-item slider-activator': item.id == selectedItem
                    },
                    'slider-activator'
                  )}
                >
                  {showCheckbox && (
                    <td className="with-cb" onClick={handleStopPropagation}>
                      <input
                        type="checkbox"
                        checked={isItemChecked(refId)}
                        onClick={() => toggleCheckbox(refId)}
                      />
                    </td>
                  )}
                  {orderedFields.map(f =>
                    editMode ? (
                      <AccessControl
                        resource={{ ...f.schemaInfo.schemaField, dataSchema }}
                        data={item}
                        selectedWorkspace={selectedWorkspace}
                      >
                        <TableCell
                          field={f}
                          item={item}
                          wrapText={wrapText}
                          editMode={editMode}
                          hoveredCell={hoveredCell}
                          handleMouseLeave={handleMouseLeave}
                          handleMouseEnter={handleMouseEnter}
                          setSelectedCell={setSelectedCell}
                          selectedCell={selectedCell}
                          onOpenItem={(itemRefId: string) =>
                            openItem(item, itemRefId)
                          }
                          getItemHref={getItemHref}
                        />
                      </AccessControl>
                    ) : (
                      <TableCell
                        key={f.field}
                        field={f}
                        item={item}
                        wrapText={wrapText}
                        editMode={editMode}
                        hoveredCell={hoveredCell}
                        handleMouseLeave={handleMouseLeave}
                        handleMouseEnter={handleMouseEnter}
                        setSelectedCell={setSelectedCell}
                        selectedCell={selectedCell}
                        disabled={disabled}
                        onOpenItem={(itemRefId: string) =>
                          openItem(item, itemRefId)
                        }
                        getItemHref={getItemHref}
                      />
                    )
                  )}
                  {allowDelete && (
                    <TD onClick={handleStopPropagation}>
                      <Icon
                        onClick={e => {
                          handleStopPropagation(e);
                          if (onDelete) onDelete(item, dataSchema);
                          else if (
                            item?.props?.refId &&
                            dataSchema?.props?.type
                          ) {
                            setDataToDelete({
                              refId: item.props.refId,
                              type: dataSchema.props.type,
                              name: item.props[dataSchema.props.displayField]
                            });
                          }
                        }}
                      >
                        <Trash />
                      </Icon>
                    </TD>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {editMode && hoveredCell?.split(' ').length > 1 && (
          <div
            onMouseEnter={() =>
              handleMouseEnter('flyoutIcon ' + hoveredCell?.split(' ')[1])
            }
            onMouseLeave={handleMouseLeave}
            className="flyout-icon"
            style={{
              top: hoveredCellPosition?.top - 55,
              right: 10
            }}
            onClick={() => {
              const item = data.filter(
                i => i.props.refId == hoveredCell?.split(' ')[1]
              );
              if (showForm) showForm(item as any);
              else updateUrl({ id: hoveredCell?.split(' ')[1] });
            }}
          >
            <Form />
          </div>
        )}
        {editMode && !!selectedCell && (
          <EditModeDropdown
            selectedCellPosition={selectedCellPosition}
            selectedCell={selectedCell}
            item={selectedCell?.item}
            setSelectedCell={setSelectedCell}
            editValue={editValue}
            setEditValue={setEditValue}
            field={selectedCell?.field}
            minEditBoxWidth={minEditBoxWidth}
            navFlyoutWidth={navFlyoutWidth}
            originalValue={originalValue}
            setOriginalValue={setOriginalValue}
            setIsTreeSelect={setIsTreeSelect}
            editValueTree={editValueTree}
            originalValueTree={originalValueTree}
            updater={updater}
            setParentTreeField={setParentTreeField}
            parentTreeField={parentTreeField}
            treeFieldGroupings={treeFieldGroupings}
            schemasProp={schemas}
            updateData={updateData}
            updateFields={updateFields}
            updateField={updateField}
            data={data}
            invalidCells={invalidCells}
            addInvalidCell={addInvalidCell}
            removeInvalidCell={removeInvalidCell}
            updatesInvalidCells={updatesInvalidCells}
            toasters={toasters}
            fields={orderedFields}
            document={document}
            setSelectedCellPosition={setSelectedCellPosition}
            totalColumnCount={totalColumnCount}
          />
        )}
        {!!data?.length && pagination?.hasMore && (
          <InfiniteLoader
            container={ref}
            loadData={increaseNumOfResultsAndLoad}
            forceResize={wrapText}
          />
        )}
      </div>

      {dataToDelete && (
        <DeleteDataModal
          onCancel={() => setDataToDelete(null)}
          onDelete={async () => {
            await deleteData(dataToDelete);
            setDataToDelete(null);
          }}
          data={dataToDelete}
        />
      )}
    </div>
  );
};

type DraggableHeaderProps = {
  col: any;
  idx: number;
  moveColumn: (fromIdx: number, toIdx: number) => void;
  setDraggedColIdx: (idx: number) => void;
  setHoveredColIdx: (idx: number) => void;
  draggedColIdx: number;
  hoveredColIdx: number;
  activeIndex: number;
  setActiveIndex: (idx: number) => void;
  wrapText: boolean;
  tableHeight: string;
};
const COLUMN_TYPE = 'COLUMN';
type DragItem = { idx: number };

const DraggableHeader = ({
  col,
  idx,
  moveColumn,
  setDraggedColIdx,
  setHoveredColIdx,
  draggedColIdx,
  hoveredColIdx,
  activeIndex,
  setActiveIndex,
  wrapText,
  tableHeight
}: DraggableHeaderProps) => {
  const isBacktaggingView = useSelector(
    s => s.view?.selected?.isBacktaggingView
  );
  const headerRef = col.ref;
  const handleRef = React.useRef(null);
  const disableDnD = isBacktaggingView && idx === 0;

  const [{ isDragging }, drag, dragPreview] = useDrag<
    DragItem,
    void,
    { isDragging: boolean }
  >({
    type: COLUMN_TYPE,
    item: { idx },
    canDrag: !disableDnD,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: COLUMN_TYPE,
    hover: (item: DragItem) => {
      if (disableDnD) return;
      if (item.idx != idx) setHoveredColIdx(idx);
    },
    drop: (item: DragItem) => {
      if (disableDnD) return;
      moveColumn(item.idx, idx);
      setDraggedColIdx(null);
      setHoveredColIdx(null);
    },
    collect: monitor => ({
      isOver: monitor.isOver({ shallow: true })
    })
  });

  dragPreview(drop(headerRef));
  drag(handleRef);
  return (
    <th
      ref={headerRef}
      key={col.field}
      className={cn({
        'dragged-col': isDragging,
        'hovered-col': isOver && draggedColIdx != null && !disableDnD
      })}
    >
      <div className="column-header">
        <span title={wrapText ? null : col.text}>{col.text}</span>
        {!disableDnD && (
          <div
            ref={handleRef}
            className={'drag-handle'}
            onMouseDown={e => {
              setDraggedColIdx(idx);
              e.stopPropagation();
            }}
          >
            <SixDots />
          </div>
        )}
      </div>
      <div
        style={{ height: tableHeight }}
        onMouseDown={e => {
          e.stopPropagation();
          setActiveIndex(idx);
        }}
        className={`resize-handle ${activeIndex == idx ? 'active' : 'idle'}`}
      />
    </th>
  );
};

type DDMProps = {
  onCancel: () => any;
  onDelete: () => Promise<any>;
  data: DataToDelete;
};
const DeleteDataModal = ({ onCancel, data, ...props }: DDMProps) => {
  const { onDelete, Loader, Alert } = useLoader(useAlert(props));
  return (
    <ConfirmModal header="Delete Data" onCancel={onCancel} onDelete={onDelete}>
      <Loader />
      <Alert />
      <div>
        Deleting this metadata will permanently remove it from any items that
        are currently tagged and could impact any views leveraging this
        metadata. Are you sure you want to proceed?
      </div>
      {data.name && (
        <div>
          - <b>{data.name}</b>
        </div>
      )}
    </ConfirmModal>
  );
};

type DataToDelete = { refId: string; type: NodeType; name: string };

type TableCellProps = {
  field: ColField;
  item: Metadata;
  wrapText: boolean;
  editMode?: boolean;
  hoveredCell?: string;
  handleMouseEnter?: any;
  handleMouseLeave?: any;
  setSelectedCell?: any;
  selectedCell?: any;
  disabled?: boolean;
  onOpenItem?: (refId: string) => void;
  getItemHref?: (refId: string) => string | null;
};

const TableCell = ({
  field,
  item,
  wrapText,
  editMode,
  hoveredCell,
  handleMouseEnter,
  handleMouseLeave,
  setSelectedCell,
  selectedCell,
  disabled,
  onOpenItem,
  getItemHref
}: TableCellProps) => {
  const { refId } = item.props;
  const { schemaInfo, isOwnField } = field;
  const { schemaField, referenceSchema, schemaType, schema } = schemaInfo;
  const showHover = field.field + ' ' + refId == hoveredCell && editMode;
  const { showEditIcon, isEditable, warningMode } = handleDisableEdit(
    schemaField,
    item,
    disabled,
    showHover
  );
  if (!isOwnField) {
    let metadata = getMetadata(item.metadata, schemaType) || [];
    let displayField = schemaField.field;
    if (referenceSchema) {
      displayField = referenceSchema.props.displayField;
      metadata = metadata.reduce((r, m) => {
        if (!m?.props) return r;
        const fieldValues = m.props[schemaField.field];
        fieldValues?.forEach(v => {
          if (!findOne(r, { id: v.id })) {
            r.push(v);
          }
        });
        return r;
      }, []);
    }
    const values = metadata
      .map(m => m.props[displayField])
      .filter(x => x)
      .sort();
    const textValues = wrapText ? null : values?.join('\n');
    return (
      <TD
        title={textValues}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        <Pills commaSeparated>
          {values?.map(m => (
            <Pill key={m} name={m} />
          ))}
        </Pills>
      </TD>
    );
  } else if (schemaField.fieldType == FieldType.reference) {
    let metadata = !referenceSchema ? [] : item.props[schemaField.field];
    if (!Array.isArray(metadata)) {
      metadata = [];
    }
    const values = metadata?.map(m => {
      if (isAuditType(schemaField.relationType)) {
        return getEmployeeName(m, { withAudit: true });
      } else {
        return m.props[referenceSchema.props.displayField];
      }
    });

    values?.sort();
    const textValues = wrapText ? null : values?.join('\n');
    return (
      <TD
        title={textValues}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        <Pills commaSeparated>
          {values?.map((v, i) => (
            <Pill key={i} name={v} />
          ))}
        </Pills>
      </TD>
    );
  } else if (schemaField.dataType == DataType.boolean) {
    return (
      <TD
        title={
          wrapText ? null : item.props[schemaField.field] ? 'True' : 'False'
        }
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        {item.props[schemaField.field] ? <TrueCheck /> : <FalseCheck />}
      </TD>
    );
  } else if (
    schemaField.dataType == DataType.date ||
    schemaField.dataType == DataType.dateTime
  ) {
    const date = parseDate(item.props[schemaField.field]);
    let value = formatDate(date);
    if (date && schemaField.dataType == DataType.dateTime) {
      value += ' ' + date.toLocaleTimeString();
    }
    return (
      <TD
        title={wrapText ? null : value}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        {value}
      </TD>
    );
  } else if (schemaField.dataType == DataType.link) {
    const fieldName = schemaField?.field;
    const value = item.props[schemaField.field];
    let icon = null;
    switch (fieldName) {
      case 'presentationLink':
        icon = <Presentation />;
        break;
      case 'googleDocumentUrl':
        icon = <GoogleDoc />;
        break;
      case 'cppItemDetailsURL':
        icon = <DocSearch />;
        break;
      case 'gcomURL':
        icon = <GIcon />;
        break;
      default:
        break;
    }
    return (
      <TD
        title={wrapText ? null : value}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn(
          {
            'edit-mode-hover': showHover
          },
          'edit-mode-container'
        )}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        {!!value?.length && (
          <a target="_blank" href={value} onClick={handleStopPropagation}>
            {!!icon ? icon : value}
          </a>
        )}
      </TD>
    );
  } else if (schemaField.dataType == DataType.enum) {
    const val = item.props[schemaField.field];
    const enumValues = schemaField.enumValues || {};
    const values = (Array.isArray(val) ? val : [val])
      .map(v => enumValues[v] || v)
      .filter(x => x);

    values?.sort();
    const textValues = wrapText ? null : values?.join('\n');

    return (
      <TD
        title={textValues}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        <Pills commaSeparated>
          {values?.map((v, i) => (
            <Pill key={i} name={v} />
          ))}
        </Pills>
      </TD>
    );
  } else {
    let vals = item.props[schemaField.field];
    if (Array.isArray(vals)) vals = vals.join(', ');
    const useContextMenuLink =
      !editMode && isOwnField && schemaField.field == schema.props.displayField;
    return (
      <TD
        title={wrapText ? null : vals}
        onMouseEnter={() => handleMouseEnter(field.field + ' ' + refId)}
        onMouseLeave={handleMouseLeave}
        className={cn('edit-mode-container', {
          'edit-mode-hover': showHover
        })}
        showEditIcon={showEditIcon}
        showHover={showHover}
        setSelectedCell={setSelectedCell}
        selectedCell={selectedCell}
        item={item}
        field={field}
        isEditable={isEditable}
        warningMode={warningMode}
      >
        {useContextMenuLink ? (
          <ContextMenuLink
            className="tabular-context-menu-link"
            href={getItemHref?.(refId)}
            onNavigate={() => onOpenItem?.(refId)}
            stopPropagation={true}
          >
            {vals}
          </ContextMenuLink>
        ) : (
          vals
        )}
      </TD>
    );
  }
};

const TD: React.FC<React.HTMLProps<HTMLTableCellElement> & TDProps> = ({
  children,
  onClick,
  title,
  className,
  onMouseEnter,
  onMouseLeave,
  showEditIcon,
  showHover,
  setSelectedCell,
  selectedCell,
  field,
  item,
  isEditable = true,
  warningMode = ''
}) => {
  const invalidCells = useSelector(s => s.view?.invalidCells);
  const editMode = useSelector(s => s.view?.editMode);
  const { createToaster, hideToasters } = useToaster(ToasterWithLink);
  const [shownToasters, setShownToasters] = React.useState<Set<string>>(
    new Set()
  );
  const [warningToastVisible, setWarningToastVisible] = React.useState(false);

  const cellId = `${item?.props?.refId}-${field?.field}`;
  const invalidCell = React.useMemo(
    () =>
      invalidCells?.find(
        cell => cell.refId === item?.props?.refId && cell.field === field?.field
      ),
    [invalidCells, item, field]
  );

  const isInvalid = !!invalidCell;
  const serverSide = invalidCell?.serverSide;

  // Get the specific error message for the invalid cell
  const errorMessage = React.useMemo(() => {
    if (!isInvalid) return null;

    return `${
      field?.schemaInfo?.schemaField.name || 'This field'
    } is required.`;
  }, [isInvalid, field]);

  //console.log('editModeFieldvalidation', editModeFieldvalidation);
  React.useEffect(() => {
    if (isInvalid && !shownToasters.has(cellId) && !serverSide) {
      const toaster = createToaster();
      toaster.error({
        header: 'Could not save this change.',
        messages: [errorMessage]
      });
      toaster.toaster.autoHide(10000);
      // Mark the toaster as shown for this cell
      setShownToasters(prev => new Set(prev).add(cellId));
      return;
    }
  }, [
    isInvalid,
    errorMessage,
    createToaster,
    cellId,
    shownToasters,
    hideToasters
  ]);

  // Reset shownToasters for this cell when it becomes valid again
  React.useEffect(() => {
    if (!isInvalid && shownToasters.has(cellId)) {
      setShownToasters(prev => {
        const next = new Set(prev);
        next.delete(cellId);
        return next;
      });
    }
  }, [isInvalid, cellId, shownToasters]);

  const handleWarningClick = React.useCallback(() => {
    if (warningToastVisible) return;
    setWarningToastVisible(true);

    const toast = createToaster();
    toast.warn({
      header: 'Warning',
      messages: [warningMode]
    });
    toast.toaster.autoHide(10000);
    setTimeout(() => setWarningToastVisible(false), 10000);
  }, [warningMode, createToaster, warningToastVisible]);

  const handleCellClick = React.useCallback(() => {
    if (warningMode !== '' && editMode) {
      handleWarningClick();
    } else if (showEditIcon) {
      setSelectedCell({ item, field });
    } else {
      onClick;
    }
  }, [
    warningMode,
    editMode,
    showEditIcon,
    handleWarningClick,
    setSelectedCell,
    item,
    field,
    onClick
  ]);

  return (
    <td
      id={
        isEditable
          ? `${item?.props?.refId}-${field?.field}`
          : `${item?.props?.refId}-${field?.field}-disabled`
      }
      onClick={handleCellClick}
      className={cn(className, {
        'hovered-cell': showHover,
        'selected-cell':
          selectedCell?.item?.props?.refId == item?.props?.refId &&
          selectedCell?.field?.field == field?.field &&
          editMode &&
          item?.props?.refId != undefined,
        'edit-not-allowed': warningMode === '' && !showEditIcon && showHover,
        'edit-allowed':
          warningMode === '' &&
          showEditIcon &&
          showHover &&
          item?.props?.refId != undefined,
        'warning-cursor': warningMode !== '' && editMode,
        'edit-mode-selected-row':
          selectedCell?.item?.props?.refId == item?.props?.refId &&
          editMode &&
          item?.props?.refId != undefined,
        'error-outline': isInvalid && editMode
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      contentEditable={showHover && selectedCell}
    >
      <span title={title}>{children}</span>
      {editMode && !!isInvalid && (
        <div className="warning-icon-container">
          <span className="warning-icon">
            <Warning />
          </span>
        </div>
      )}
      {showHover && (
        <div>
          {showEditIcon ? (
            <span className="edit-icon">
              <LargePencil />
            </span>
          ) : null}
        </div>
      )}
    </td>
  );
};
function getTableInfo(
  card: View.Card,
  dataSchema: Schema,
  schemas: Schema[],
  userSetting: UserSetting
) {
  const cardFields = getCardFields(card, userSetting);
  const headerRefs = React.useMemo(
    () => cardFields.map(() => React.createRef<HTMLTableCellElement>()),
    [cardFields.length]
  );
  if (dataSchema) {
    let s = dataSchema;
    let df = findOne(cardFields, {
      field: `${s.props.type}.${s.props.displayField}`
    });
    if (!df && s.baseSchema) {
      df = findOne(cardFields, {
        field: `${s.baseSchema.props.type}.${s.baseSchema.props.displayField}`
      });
    }
    if (!df) {
      cardFields.unshift({ field: `${s.props.type}.${s.props.displayField}` });
    }
  }

  const fields = cardFields.reduce((p, f) => {
    let _f = parseField(f.field, schemas);
    if (_f.schemaField)
      p.push({
        ...f,
        schemaInfo: _f,
        isOwnField: _f.schemaType == dataSchema?.props?.type
      });
    return p;
  }, [] as ColField[]);

  const headers = fields.map(f => {
    if (f.schemaInfo.schemaType != dataSchema?.props?.type) {
      return f.schemaInfo.schemaField.displayName;
    }
    return f.schemaInfo.schemaField.name;
  });

  const headerType = fields.map(f => {
    return f?.schemaInfo?.schemaField?.dataType;
  });

  const columns = createHeaders(headers, headerType, fields, headerRefs);

  return { fields, columns };
}

const getGridTemplateColumns = (
  orderedColumns: {
    text: string;
    colType: string;
    field: string;
    ref: any;
  }[],
  columnWidths: { [key: string]: number },
  showCheckbox: boolean,
  allowDelete: boolean
) => {
  const cols = orderedColumns.map(col => {
    const width = columnWidths[col.field];
    if (width) return `${width}px`;
    if (col.colType == 'boolean' || col.colType == 'number')
      return 'minmax(120px, 2fr)';
    if (col.colType == 'StatusCategory') return 'minmax(120px, 3fr)';
    return 'minmax(150px, 5fr)';
  });
  if (allowDelete) cols.push('21px');
  if (showCheckbox) cols.unshift('25px');
  return cols;
};

const handleStopPropagation = (e: any) => e.stopPropagation();
export interface TDProps {
  showEditIcon?: boolean;
  showHover?: boolean;
  setSelectedCell?: any;
  selectedCell?: any;
  field?: ColField;
  item?: Metadata;
  toaster?: any;
  isEditable?: boolean;
  warningMode?: string;
}
